import React, { useMemo } from "react";
import { Modal, View, TouchableOpacity, StyleSheet } from "react-native";
import MapView, { Marker, PROVIDER_GOOGLE } from "react-native-maps";
import Text from "@components/CustomText";
import { tokens } from "../styles";

// Full-screen map modal. Uses custom Marker children (a colored circle
// with a thin white border) instead of pinColor — pinColor honors only a
// discrete set on iOS, so cyan/yellow/pink fall back to red. Custom
// children render reliably on both platforms.
//
// Markers are color-coded by crate id (round-robin) so customers across
// connected crates are visually distinguishable.

const MARKER_COLORS = [
  "#EF4444",
  "#3B82F6",
  "#22C55E",
  "#A855F7",
  "#F97316",
  "#EAB308",
  "#EC4899",
  "#06B6D4",
];

const DEFAULT_CENTER = { lat: 28.4595, lng: 77.0266 };

const buildMarkerData = (markers = []) => {
  const order = [];
  const seen = new Set();
  markers.forEach((m) => {
    const id = String(m?.crate_id ?? "default");
    if (!seen.has(id)) {
      seen.add(id);
      order.push(id);
    }
  });
  return markers
    .map((m, idx) => {
      const id = String(m?.crate_id ?? "default");
      const colorIdx = order.indexOf(id);
      const lat = Number(m?.lat);
      const lng = Number(m?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return {
        key: `${id}-${idx}`,
        lat,
        lng,
        label: m?.label || "",
        color: MARKER_COLORS[colorIdx % MARKER_COLORS.length],
      };
    })
    .filter(Boolean);
};

const MapSheet = ({ visible, markers = [], onClose }) => {
  const data = useMemo(() => buildMarkerData(markers), [markers]);

  const initialRegion = useMemo(() => {
    if (data.length === 0) {
      return {
        latitude: DEFAULT_CENTER.lat,
        longitude: DEFAULT_CENTER.lng,
        latitudeDelta: 0.2,
        longitudeDelta: 0.2,
      };
    }
    const first = data[0];
    return {
      latitude: first.lat,
      longitude: first.lng,
      latitudeDelta: data.length > 1 ? 0.08 : 0.01,
      longitudeDelta: data.length > 1 ? 0.08 : 0.01,
    };
  }, [data]);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      transparent={false}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <Text variant="title_16" style={styles.title}>
            Map
          </Text>
          <TouchableOpacity
            onPress={onClose}
            activeOpacity={0.85}
            style={styles.closeBtn}
          >
            <Text variant="caption_2" style={styles.closeText}>
              Close
            </Text>
          </TouchableOpacity>
        </View>
        {data.length === 0 ? (
          <View style={styles.empty}>
            <Text variant="caption_2" style={styles.emptyText}>
              No markers to display.
            </Text>
          </View>
        ) : (
          <MapView
            provider={PROVIDER_GOOGLE}
            style={styles.map}
            initialRegion={initialRegion}
          >
            {data.map((m) => (
              <Marker
                key={m.key}
                coordinate={{ latitude: m.lat, longitude: m.lng }}
                title={m.label}
              >
                <View
                  style={[styles.markerDot, { backgroundColor: m.color }]}
                />
              </Marker>
            ))}
          </MapView>
        )}
      </View>
    </Modal>
  );
};

export default MapSheet;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: tokens.surface },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: tokens.spacing.medium,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  title: { color: tokens.textPrimary, fontWeight: "700" },
  closeBtn: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 6,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.borderMedium,
  },
  closeText: { color: tokens.textPrimary, fontSize: 12, fontWeight: "600" },
  map: { flex: 1 },
  empty: { flex: 1, alignItems: "center", justifyContent: "center" },
  emptyText: { color: tokens.textTertiary, fontSize: 14 },
  markerDot: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: tokens.surface,
  },
});
